import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { FefoBadge, ExpiryBadge } from "@/components/StatusBadges";
import ExportDialog from "@/components/ExportDialog";
import { CheckCircle2, AlertTriangle, Beaker } from "lucide-react";
import { toast } from "sonner";

export default function Consumption() {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["items", "consumable"], queryFn: () => api.get("/items?kind=consumable").then((r) => r.data) });
  const { data: logs = [] } = useQuery({ queryKey: ["consumption"], queryFn: () => api.get("/consumption").then((r) => r.data) });

  const [itemId, setItemId] = useState("");
  const [batchId, setBatchId] = useState("");
  const [qty, setQty] = useState(1);
  const [purpose, setPurpose] = useState("test");

  const { data: batches = [] } = useQuery({
    queryKey: ["batches", itemId],
    queryFn: () => api.get(`/inventory/batches?item_id=${itemId}`).then((r) => r.data),
    enabled: !!itemId,
  });

  const selectItem = (v) => {
    setItemId(v);
    setBatchId("");
  };

  // auto-select FEFO batch when list loads
  const fefoBatch = batches.find((b) => b.is_fefo) || batches[0];
  useEffect(() => {
    if (batches.length && !batchId && fefoBatch) setBatchId(fefoBatch._id);
  }, [batches, batchId, fefoBatch]);

  const selectedBatch = batches.find((b) => b._id === batchId);
  const isOverride = selectedBatch && !selectedBatch.is_fefo;

  const submit = async () => {
    if (!itemId || !batchId) { toast.error("Select item and batch"); return; }
    try {
      const { data } = await api.post("/consumption", {
        item_id: itemId, batch_id: batchId, qty: Number(qty), purpose, fefo_override: !!isOverride,
      });
      if (data.fefo_override) toast.warning("FEFO override logged — an earlier-expiring batch was available");
      else toast.success("Consumption logged (FEFO-compliant)");
      if (data.low_stock) toast.error(`Low stock alert triggered! ${data.remaining} units remaining`);
      setQty(1); setBatchId(""); setItemId("");
      qc.invalidateQueries({ queryKey: ["consumption"] });
      qc.invalidateQueries({ queryKey: ["batches"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="consumption-page">
      <div>
        <h2 className="font-serif text-3xl tracking-tight text-navy">Daily Consumption Entry</h2>
        <p className="text-slate-500 text-sm mt-1">Log tests, controls &amp; calibrators used. Batch is auto-suggested via FEFO.</p>
      </div>

      <Card className="p-6 bg-white border-slate-200 max-w-3xl">
        <div className="flex items-center gap-2 mb-4 text-navy">
          <Beaker size={20} /> <span className="font-serif text-xl tracking-tight">Log Usage</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Label>Item</Label>
            <Select value={itemId} onValueChange={selectItem}>
              <SelectTrigger data-testid="consumption-item-select"><SelectValue placeholder="Select item used" /></SelectTrigger>
              <SelectContent className="bg-white max-h-64">
                {items.map((i) => <SelectItem key={i._id} value={i._id}>{i.name} ({i.category})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {itemId && (
            <div className="sm:col-span-2">
              <Label>Batch / Lot</Label>
              <div className="space-y-1.5 mt-1">
                {batches.length === 0 && <div className="text-sm text-slate-400">No stock available for this item.</div>}
                {batches.map((b) => (
                  <button
                    key={b._id}
                    type="button"
                    data-testid={`consumption-batch-${b.is_fefo ? "fefo" : "other"}`}
                    onClick={() => setBatchId(b._id)}
                    className="w-full flex flex-wrap items-center gap-2 px-3 py-2.5 rounded-lg border text-left transition-colors duration-200"
                    style={{
                      borderColor: batchId === b._id ? "#042C53" : b.is_fefo ? "#10B981" : "#e2e8f0",
                      borderWidth: batchId === b._id || b.is_fefo ? 2 : 1,
                      background: batchId === b._id ? "#F0F9FF" : "#fff",
                    }}
                  >
                    <span className="font-mono text-xs font-semibold text-navy">{b.lot_number}</span>
                    {b.is_fefo && <FefoBadge />}
                    <ExpiryBadge state={b.expiry_state} days={b.days_to_expiry} />
                    <span className="text-xs text-slate-500 ml-auto">{b.qty_available} available</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Quantity</Label>
            <Input type="number" data-testid="consumption-qty" value={qty} min={1} onChange={(e) => setQty(e.target.value)} />
          </div>
          <div>
            <Label>Purpose</Label>
            <Select value={purpose} onValueChange={setPurpose}>
              <SelectTrigger data-testid="consumption-purpose"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="control">Control</SelectItem>
                <SelectItem value="calibrator">Calibrator</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isOverride && (
          <div className="mt-4 flex items-start gap-2 p-3 rounded-lg" style={{ background: "#FFFBEB", border: "1px solid #F59E0B55" }} data-testid="fefo-warning">
            <AlertTriangle size={18} style={{ color: "#F59E0B" }} className="mt-0.5" />
            <span className="text-sm text-slate-700">
              <strong>FEFO warning:</strong> An earlier-expiring batch is still in stock. Using this batch will be logged as an override.
            </span>
          </div>
        )}

        <Button onClick={submit} data-testid="consumption-submit" className="mt-5 text-white" style={{ background: "#042C53" }}>
          <CheckCircle2 size={16} className="mr-2" /> Log Consumption
        </Button>
      </Card>

      <div className="flex items-center justify-between">
        <h3 className="font-serif text-2xl tracking-tight text-navy">My Consumption Log</h3>
        <ExportDialog dataset="consumption" testid="consumption-export" />
      </div>
      <Card className="bg-white border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 border-b border-slate-200 bg-slate-50">
                <th className="px-4 py-3 font-semibold">Date/Time (IST)</th>
                <th className="px-4 py-3 font-semibold">Item</th>
                <th className="px-4 py-3 font-semibold text-right">Qty</th>
                <th className="px-4 py-3 font-semibold">Purpose</th>
                <th className="px-4 py-3 font-semibold">Batch</th>
                <th className="px-4 py-3 font-semibold">FEFO</th>
                <th className="px-4 py-3 font-semibold">Logged By</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((t, idx) => (
                <tr key={t._id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`consumption-log-${idx}`}>
                  <td className="px-4 py-3 text-slate-600">{new Date(t.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}</td>
                  <td className="px-4 py-3 font-medium text-navy">{t.item_name}</td>
                  <td className="px-4 py-3 text-right">{t.qty} {t.uom}</td>
                  <td className="px-4 py-3 text-slate-600 capitalize">{t.reason_code}</td>
                  <td className="px-4 py-3 text-slate-600 font-mono text-xs">{t.notes}</td>
                  <td className="px-4 py-3">
                    {t.fefo_override
                      ? <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: "#F59E0B" }}>Override</span>
                      : <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: "#10B981" }}>OK</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{t.performed_by_name}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-400">No consumption logged yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
